import { IconButton } from "@material-ui/core";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import * as React from "react";

// util
import {
  IEventCategoryColoring,
  IEventColoringSettings,
} from "@clockwise/client-commons/src/util/event-category-coloring";
import { EventPositioner } from "@clockwise/client-commons/src/util/event-position";
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";

// internal
import { styles } from "./CalendarWeek.styles";

// components
import { CalendarDay, CalendarDayEvent, CalendarHours } from "@components/calendar";
import { CalendarDayAugmentedEvent } from "../calendar-day";
import { CalendarDayEventMode } from "../calendar-day-event";
import { IWorkingHoursDaySetting } from "../renderers";

export interface DayEvents {
  day: ZonedMoment;
  normalEvents: CalendarDayAugmentedEvent[];
  allDayEvents: CalendarDayAugmentedEvent[];
  allDayEventsMaxCount: number;
}

export interface CalendarWeekProps extends WithStyles<typeof styles> {
  onClick: (calendarId: string, externalEventId: string) => void;
  onClickDay: (moment: ZonedMoment) => void;
  userId: string;
  calendarIds: string[];
  colorSettings?: IEventColoringSettings<IEventCategoryColoring>;
  eventsByDay: DayEvents[];
  daySettings?: IWorkingHoursDaySetting[];
  showFragmentedTime?: boolean;
  showWorkingHours?: boolean;
  hideDayHeaders?: boolean;
  showShortHours?: boolean;
  containerStyle?: React.CSSProperties;
  dayContainerStyle?: React.CSSProperties;
  hourTextStyle?: React.CSSProperties;
  hourTextContainerStyle?: React.CSSProperties;
  horizontalGridStyle?: React.CSSProperties;
  showHoursBorder?: boolean;
  mode?: CalendarDayEventMode;
  eventContainerChildren?: JSX.Element | null;
  stickyHeaderChildren?: JSX.Element | null;
}

export interface CalendarWeekState {
  isExpanded: boolean;
}

class CalendarWeekComponent extends React.Component<CalendarWeekProps, CalendarWeekState> {
  constructor(props: CalendarWeekProps) {
    super(props);

    this.state = {
      isExpanded: false,
    };
  }

  public render() {
    const {
      eventContainerChildren,
      stickyHeaderChildren,
      containerStyle,
      dayContainerStyle,
      hourTextStyle,
      hourTextContainerStyle,
      horizontalGridStyle,
      daySettings,
      showFragmentedTime,
      showWorkingHours,
      showShortHours,
      showHoursBorder,
      userId,
      calendarIds,
      colorSettings,
      mode,
    } = this.props;

    const { isExpanded } = this.state;

    const allDayEventsMaxCount =
      (this.props.eventsByDay.length && this.props.eventsByDay[0].allDayEventsMaxCount) || 0;

    return (
      <div className={this.props.classes.container} style={containerStyle}>
        {!this.props.hideDayHeaders ? (
          <div className="sticky-day-headers">
            <div className={this.props.classes.allDayExpandContainer}>
              {allDayEventsMaxCount > 2 ? (
                <IconButton onClick={() => this.setState({ isExpanded: !isExpanded })}>
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              ) : null}
            </div>
            {this.props.eventsByDay.map(({ day, allDayEvents }) => {
              const isToday = new ZonedMoment().isSame(day, "days");

              const hasTooltipOptions = !!allDayEvents.find((e) => e.tooltipOptions);

              return (
                <div key={day.format("MM-DD-YYYY")} className={this.props.classes.dayHeaderText}>
                  <div
                    className={`circle ${isToday ? " today" : ""}`}
                    onClick={() => this.props.onClickDay(day)}
                  >
                    <div className="day-abbr">{day.format("ddd")}</div>
                    <div className="day">{day.format("D")}</div>
                  </div>
                  {!allDayEvents.length ? (
                    <div style={{ position: "relative", width: "100%", height: 30 }} />
                  ) : (
                    <div
                      style={{
                        maxHeight: 220,
                        overflow: hasTooltipOptions ? "visible" : "auto",
                        width: "100%",
                      }}
                    >
                      {allDayEvents
                        .slice(0, !isExpanded ? 2 : undefined)
                        .map(({ event, tooltipOptions }) => {
                          const ep = new EventPositioner(event, [day]);

                          if (tooltipOptions) {
                            tooltipOptions.tipPosition = day.day() < 3 ? "left" : "right";
                          }

                          return (
                            <div style={{ position: "relative", width: "100%", height: 30 }}>
                              <CalendarDayEvent
                                calendarIds={calendarIds}
                                colorSettings={colorSettings}
                                eventPositioner={ep}
                                mode="simple-bold"
                                onClick={this.props.onClick}
                                tooltipOptions={tooltipOptions}
                                userId={userId}
                              />
                            </div>
                          );
                        })}
                      {!isExpanded && allDayEvents.length > 2 ? (
                        <div
                          className={this.props.classes.allDayExpandDay}
                          onClick={() => this.setState({ isExpanded: !isExpanded })}
                        >
                          {allDayEvents.length - 2} more
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
            {stickyHeaderChildren || null}
          </div>
        ) : null}
        <div className="calendar-container">
          <CalendarHours
            showShortHours={showShortHours}
            showBorder={showHoursBorder}
            hourTextStyle={hourTextStyle}
            hourTextContainerStyle={hourTextContainerStyle}
          />
          <div className={this.props.classes.horizontalGridContainer}>
            {new Array(25).fill(null).map((_n, i) => {
              const top = i === 0 ? -1 : `${(100 * i) / 24}%`;

              return (
                <div
                  key={i}
                  className={this.props.classes.horizontalGrid}
                  style={{ top, ...horizontalGridStyle }}
                />
              );
            })}
          </div>
          <div style={{ display: "flex", flexDirection: "row", flex: 1, position: "relative" }}>
            {this.props.eventsByDay.map(({ day, normalEvents }, i) => (
              <div
                key={day.format("MM-DD-YYYY")}
                className={this.props.classes.dayContainer}
                style={dayContainerStyle}
              >
                <CalendarDay
                  day={day}
                  events={normalEvents}
                  colorSettings={this.props.colorSettings}
                  calendarIds={this.props.calendarIds}
                  userId={this.props.userId}
                  onClick={this.props.onClick}
                  daySetting={daySettings && daySettings[i]}
                  showFragmentedTime={showFragmentedTime}
                  showWorkingHours={showWorkingHours}
                  mode={mode}
                />
              </div>
            ))}
            {eventContainerChildren || null}
          </div>
        </div>
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////
export const CalendarWeek = withStyles(styles)(CalendarWeekComponent);
